import * as React from 'react';
import { MediaQuery, Link, DateTime, Image, Util, Button, RichTextArea, Carousel, LazyImage, Modal, Accordion, AccordionItem, SkeletonLoader } from '@grandvisionhq/common';
import { Container, Heading, Text, Hr } from '@grandvisionhq/elements';
import classnames from 'classnames';
import { useInView } from 'react-intersection-observer';
import { Link as Link$1 } from 'react-scroll';
import { Icon } from '@grandvisionhq/icons';
import { useIntl } from '@grandvisionhq/state';

const Picture = ({ url , mobileUrl , alt , className  })=>/*#__PURE__*/ React.createElement("picture", {
        className: classnames('picture', className)
    }, mobileUrl && /*#__PURE__*/ React.createElement("source", {
        media: `(max-width: ${MediaQuery.Breakpoints.M}px)`,
        srcSet: mobileUrl
    }), /*#__PURE__*/ React.createElement("source", {
        media: `(min-width: ${MediaQuery.Breakpoints.M})`,
        srcSet: url
    }), /*#__PURE__*/ React.createElement("img", {
        className: "picture__image",
        src: url,
        alt: alt
    }))
;

const LazyPicture = ({ image , mobileImage , className  })=>{
    const isPhone = MediaQuery.useIsPhone();
    const { h , w  } = isPhone && mobileImage ? mobileImage.dimensions : image.dimensions;
    const [ref, inView] = useInView({
        rootMargin: '150px 0px',
        triggerOnce: true
    });
    return(/*#__PURE__*/ React.createElement("div", {
        ref: ref,
        className: classnames('lazy-picture', {
            'lazy-picture--loaded': inView
        }, className),
        style: {
            paddingBottom: `${(h / w * 100).toFixed(3)}%`
        }
    }, inView && /*#__PURE__*/ React.createElement(Picture, {
        className: "lazy-picture__image",
        mobileUrl: mobileImage === null || mobileImage === void 0 ? void 0 : mobileImage.url,
        url: image.url,
        alt: image.alt
    })));
};

const Banner = ({ image , mobileImage , link , useContainer =false , useLazyLoading =true ,  })=>{
    const ContainerComponent = useContainer ? Container : React.Fragment;
    if (!image) {
        console.warn('Cannot render Banner: missing image');
        return null;
    }
    return(/*#__PURE__*/ React.createElement("div", {
        className: "banner"
    }, /*#__PURE__*/ React.createElement(ContainerComponent, null, useLazyLoading ? /*#__PURE__*/ React.createElement(LazyPicture, {
        mobileImage: mobileImage,
        image: image
    }) : /*#__PURE__*/ React.createElement(Picture, {
        mobileUrl: mobileImage === null || mobileImage === void 0 ? void 0 : mobileImage.url,
        url: image.url,
        alt: image.alt
    }), link && /*#__PURE__*/ React.createElement(Link, {
        hidden: true,
        url: link.url,
        target: link.target,
        className: "banner__link"
    }, link.label && /*#__PURE__*/ React.createElement("span", {
        className: "banner__link-text"
    }, link.label)))));
};

const BlogMeta = ({ publishDate , readingTime =0 , minutesLabel , locale  })=>{
    const hasReadingTime = readingTime && readingTime > 0 && minutesLabel;
    if (!publishDate) {
        return null;
    }
    return(/*#__PURE__*/ React.createElement("div", {
        className: "blog-meta"
    }, /*#__PURE__*/ React.createElement("span", {
        className: "blog-meta__date"
    }, /*#__PURE__*/ React.createElement(DateTime, {
        dateTime: publishDate,
        locale: locale
    })), hasReadingTime && /*#__PURE__*/ React.createElement("span", {
        className: "blog-meta__minutes"
    }, `- ${readingTime.toString()} ${minutesLabel}`)));
};

const BlogCard = ({ image , mobileImage , title , locale , readingTime , publishDate , summary , url , minutesLabel  })=>{
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const imageUrl = isSmallScreen && mobileImage ? mobileImage.url : image === null || image === void 0 ? void 0 : image.url;
    if (!image && !mobileImage && !title && !summary) {
        console.error('BlogCard: missing content, please supply an image, mobileImage, title or summary');
        return null;
    }
    return(/*#__PURE__*/ React.createElement("a", {
        className: "blog-card",
        href: url
    }, /*#__PURE__*/ React.createElement("div", {
        className: "blog-card__content"
    }, image && imageUrl && /*#__PURE__*/ React.createElement(Image, {
        className: "blog-card__image",
        url: imageUrl,
        alt: image.alt,
        cover: true
    }), /*#__PURE__*/ React.createElement("div", {
        className: "blog-card__text"
    }, title && /*#__PURE__*/ React.createElement(Heading, {
        variant: "smallest",
        component: "h2",
        className: "blog-card__title"
    }, Util.truncateString(title, 50)), summary && /*#__PURE__*/ React.createElement("p", {
        className: "blog-card__paragraph"
    }, Util.truncateString(summary, 200)), /*#__PURE__*/ React.createElement(BlogMeta, {
        publishDate: publishDate,
        readingTime: readingTime,
        minutesLabel: minutesLabel,
        locale: locale
    })))));
};
var BlogCard$1 = /*#__PURE__*/ React.memo(BlogCard);

// fixed navigation height needed for anchor scrolling
const siteNavHeightOffset = -64;
const BlogDetailHeader = ({ image , mobileImage , title , scrollTo  })=>{
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const imageUrl = isSmallScreen && mobileImage ? mobileImage.url : image.url;
    if (!imageUrl) {
        return null;
    }
    return(/*#__PURE__*/ React.createElement("div", {
        className: "blog-detail-header",
        style: {
            backgroundImage: `url(${imageUrl})`
        }
    }, /*#__PURE__*/ React.createElement(Heading, {
        className: "blog-detail-header__title"
    }, title), /*#__PURE__*/ React.createElement(Link$1, {
        className: "blog-detail-header__link",
        href: "#",
        to: scrollTo,
        offset: siteNavHeightOffset,
        smooth: true,
        duration: 500
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "chevron-down"
    }))));
};

const BlogHero = ({ image , mobileImage , title , summary , link , readMoreLabel ,  })=>{
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    var ref;
    const imageUrl = isSmallScreen && mobileImage ? mobileImage.url : (ref = image === null || image === void 0 ? void 0 : image.url) !== null && ref !== void 0 ? ref : '';
    const textLength = isSmallScreen ? 100 : 150;
    return(/*#__PURE__*/ React.createElement("div", {
        className: "blog-hero",
        style: {
            backgroundImage: `url(${imageUrl})`
        }
    }, /*#__PURE__*/ React.createElement(Container, {
        className: "site-container"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "blog-hero__content"
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "large",
        className: "blog-hero__title"
    }, title), summary && /*#__PURE__*/ React.createElement(Text, {
        className: "blog-hero__intro"
    }, Util.truncateString(summary, textLength)), /*#__PURE__*/ React.createElement(Button, {
        size: "medium",
        href: link,
        className: "blog-hero__button",
        variant: "outline",
        caption: readMoreLabel,
        "aria-label": readMoreLabel
    })))));
};
var BlogHero$1 = /*#__PURE__*/ React.memo(BlogHero);

const BlogIntro = ({ summary , publishDate , readingTime , minutesLabel , locale  })=>/*#__PURE__*/ React.createElement("div", {
        className: "blog-intro"
    }, summary && /*#__PURE__*/ React.createElement("p", {
        className: "blog-intro__summary"
    }, summary), /*#__PURE__*/ React.createElement(BlogMeta, {
        publishDate: publishDate,
        readingTime: readingTime,
        minutesLabel: minutesLabel,
        locale: locale
    }))
;

const BlogList = ({ title , children , noArticlesLabel  })=>/*#__PURE__*/ React.createElement("div", {
        className: "blog-list"
    }, /*#__PURE__*/ React.createElement(Heading, {
        component: "h2",
        className: "blog-list__title"
    }, title), children ? /*#__PURE__*/ React.createElement("div", {
        className: "blog-list__wrapper"
    }, children) : /*#__PURE__*/ React.createElement(Heading, {
        variant: "smallest",
        className: "blog-list__message"
    }, noArticlesLabel))
;

const BrandList = ({ title , children , className  })=>/*#__PURE__*/ React.createElement("div", {
        className: classnames('brand-list', className)
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "small",
        className: "heading brand-list__title"
    }, title), /*#__PURE__*/ React.createElement("ul", {
        className: "brand-list__content"
    }, children))
;

const BrandListGroup = ({ title , children , className  })=>{
     return React.createElement("li", {
        className: classnames('brand-list-group', className)
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "subheading1",
        className: classnames('brand-list-group__title', {
            'brand-list-group__title--empty': !(title === null || title === void 0 ? void 0 : title.length)
        })
    }, title), children);
};

const BrandListItem = ({ url , children , className  })=>/*#__PURE__*/ React.createElement("a", {
        className: classnames('brand-list-item', className),
        href: url
    }, children)
;
var BrandListItem$1 = /*#__PURE__*/ React.memo(BrandListItem);

const ExclusiveBrands = ({ children  })=>/*#__PURE__*/ React.createElement("div", {
        className: "exclusive-brands"
    }, /*#__PURE__*/ React.createElement("ul", {
        className: "exclusive-brands__brand-list"
    }, children))
;

const ExclusiveBrandsItem = ({ logoSrc , title , url  })=>/*#__PURE__*/ React.createElement("li", {
        className: "exclusive-brands-item"
    }, /*#__PURE__*/ React.createElement("a", {
        className: "exclusive-brands-item__link",
        href: url
    }, /*#__PURE__*/ React.createElement("img", {
        className: "exclusive-brands-item__image",
        src: logoSrc,
        alt: title
    })))
;
var ExclusiveBrandsItem$1 = /*#__PURE__*/ React.memo(ExclusiveBrandsItem);

function _extends$3() {
    _extends$3 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$3.apply(this, arguments);
}
const SUPPORTED_STYLES = [
    'primary',
    'secondary',
    'primary outline',
    'secondary outline'
];
const ContentBanner = ({ image , mobileImage , title , text , links , textColor ='white' ,  })=>{
    const isPhone = MediaQuery.useIsPhone();
    const imageUrl = isPhone ? mobileImage === null || mobileImage === void 0 ? void 0 : mobileImage.url : image === null || image === void 0 ? void 0 : image.url;
    const imageLink = links.find(({ style  })=>style === 'image'
    );
    const [ref, inView] = useInView({
        rootMargin: '150px 0px',
        triggerOnce: true
    });
    const btnLinks = React.useMemo(()=>{
        return links.filter(({ style  })=>SUPPORTED_STYLES.includes(style !== null && style !== void 0 ? style : '')
        ).map(({ url , label , style , target  }, i)=>/*#__PURE__*/ React.createElement(Button, _extends$3({
                key: i,
                href: url,
                caption: label,
                className: "content-banner__button",
                hrefTarget: target === 'external' ? '_blank' : undefined
            }, Util.createButtonStyles(style)))
        );
    }, [
        links
    ]);
    if (!image && !title && !text) {
        console.warn('Cannot render ContentBanner: missing content');
        return null;
    }
    return(/*#__PURE__*/ React.createElement("div", {
        ref: ref,
        className: `content-banner content-banner--color-${textColor}`,
        style: inView && imageUrl ? {
            backgroundImage: `url(${imageUrl})`
        } : undefined
    }, /*#__PURE__*/ React.createElement(Container, {
        className: "content-banner__container"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "content-banner__body"
    }, title && /*#__PURE__*/ React.createElement(Heading, {
        className: "content-banner__title",
        component: "h2",
        variant: "large"
    }, title), text && /*#__PURE__*/ React.createElement(Text, {
        className: "content-banner__text"
    }, text), btnLinks, imageLink && /*#__PURE__*/ React.createElement(Link, {
        hidden: true,
        url: imageLink.url,
        target: imageLink.target,
        className: "content-banner__image-link"
    }, imageLink.label && /*#__PURE__*/ React.createElement("span", {
        className: "content-banner__image-link-text"
    }, imageLink.label))))));
};

const HeadTags = ({ description , ogUrl , ogImage , headComponent: Component = React.Fragment , title , hreflangs , noFollow , noIndex , children ,  })=>/*#__PURE__*/ React.createElement(Component, null, /*#__PURE__*/ React.createElement("title", null, title), ogUrl && /*#__PURE__*/ React.createElement("link", {
        rel: "canonical",
        href: ogUrl
    }), hreflangs.map(({ url , locale  })=>/*#__PURE__*/ React.createElement("link", {
            rel: "alternate",
            key: url,
            hrefLang: locale,
            href: url
        })
    ), /*#__PURE__*/ React.createElement("meta", {
        name: "robots",
        content: `${noIndex ? 'noindex' : 'index'}, ${noFollow ? 'nofollow' : 'follow'}`
    }), /*#__PURE__*/ React.createElement("meta", {
        property: "og:url",
        content: ogUrl
    }), /*#__PURE__*/ React.createElement("meta", {
        property: "og:title",
        content: title
    }), /*#__PURE__*/ React.createElement("meta", {
        name: "description",
        content: description
    }), /*#__PURE__*/ React.createElement("meta", {
        property: "og:description",
        content: description
    }), /*#__PURE__*/ React.createElement("meta", {
        name: "twitter:site",
        content: ogUrl
    }), /*#__PURE__*/ React.createElement("meta", {
        name: "twitter:card",
        content: "summary_large_image"
    }), /*#__PURE__*/ React.createElement("meta", {
        name: "twitter:image",
        content: ogImage
    }), /*#__PURE__*/ React.createElement("meta", {
        property: "og:image",
        content: ogImage
    }), /*#__PURE__*/ React.createElement("meta", {
        property: "og:image:width",
        content: "1200"
    }), /*#__PURE__*/ React.createElement("meta", {
        property: "og:image:height",
        content: "630"
    }), children)
;

const regHex = /#([a-f0-9]{3}){1,2}\b/i;
const regPresetBg = /black|grey|white/;
const RichBanner = ({ image , mobileImage , link , richText , layout , bannerWidth , imageWidth , imageAlignment ='left' , backgroundColor ='transparent' , textColor ='black' , children  })=>{
    if (!image && !richText) {
        console.warn(`Cannot render RichBanner: missing content`);
        return null;
    }
    const ContainerComponent = bannerWidth === 'contained' ? Container : React.Fragment;
    const classNames = classnames('rich-banner', `rich-banner--image-alignment-${imageAlignment}`, `rich-banner--text-color-${textColor}`, {
        [`rich-banner--image-width-${imageWidth}`]: imageWidth,
        [`rich-banner--layout-${layout}`]: layout
    });
    const richBannerStyle = {
        backgroundColor: regHex.test(backgroundColor) ? backgroundColor : regPresetBg.test(backgroundColor) ? Util.colorHexMap[backgroundColor] : 'transparent'
    };
    const bannerContent = /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: "rich-banner__content"
    }, richText && /*#__PURE__*/ React.createElement(RichTextArea, {
        className: "rich-banner__rich-text",
        content: richText,
        textColor: textColor
    })), children && /*#__PURE__*/ React.createElement("div", {
        className: "rich-banner__children"
    }, children));
    return(/*#__PURE__*/ React.createElement(ContainerComponent, null, /*#__PURE__*/ React.createElement("div", {
        className: classNames,
        style: richBannerStyle
    }, image && /*#__PURE__*/ React.createElement("div", {
        className: "rich-banner__slot rich-banner__slot--primary"
    }, link ? /*#__PURE__*/ React.createElement(Link, {
        className: "rich-banner__link",
        url: link.url,
        target: link.target
    }, link.label && /*#__PURE__*/ React.createElement("span", {
        className: "rich-banner__link-text"
    }, link.label), /*#__PURE__*/ React.createElement(LazyPicture, {
        className: "rich-banner__picture",
        mobileImage: mobileImage,
        image: image
    })) : /*#__PURE__*/ React.createElement(LazyPicture, {
        className: "rich-banner__picture",
        mobileImage: mobileImage,
        image: image
    })), richText && /*#__PURE__*/ React.createElement("aside", {
        className: "rich-banner__slot rich-banner__slot--secondary"
    }, bannerWidth !== 'contained' && imageWidth === '100' ? /*#__PURE__*/ React.createElement(Container, null, bannerContent) : /*#__PURE__*/ React.createElement(React.Fragment, null, bannerContent)))));
};

const CategoryLinks = ({ children  })=>{
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    if (!children || !Array.isArray(children)) {
        return null;
    }
    return(/*#__PURE__*/ React.createElement("article", {
        className: "category-links"
    }, /*#__PURE__*/ React.createElement(Container, null, /*#__PURE__*/ React.createElement("div", {
        className: "category-links__content"
    }, isSmallScreen ? /*#__PURE__*/ React.createElement(Carousel, {
        pagination: true
    }, children) : children))));
};

const CategoryLinksItem = ({ title , link , image  })=>{
    if (!image && !title) {
        console.warn(`Cannot render CategoryLink: missing content`);
        return null;
    }
    return(/*#__PURE__*/ React.createElement("section", {
        className: "category-links-item"
    }, /*#__PURE__*/ React.createElement("a", {
        className: "category-links-item__link",
        href: link.url
    }, /*#__PURE__*/ React.createElement("div", {
        className: "category-links-item__image-container"
    }, image && /*#__PURE__*/ React.createElement(LazyImage, {
        rootMargin: "50px 0px",
        src: image.url,
        alt: image.alt || '',
        width: image.dimensions.w || 274,
        height: image.dimensions.h || 108
    })), title && /*#__PURE__*/ React.createElement("span", {
        className: "category-links-item__title"
    }, title))));
};

const Certificates = ({ children  })=>{
    if (!children) {
        return null;
    }
    return(/*#__PURE__*/ React.createElement("div", {
        className: "certificates"
    }, /*#__PURE__*/ React.createElement(Container, null, /*#__PURE__*/ React.createElement("article", {
        className: "certificates__list"
    }, children))));
};

const CertificatesItem = ({ link , image , title , text  })=>{
    if (!image) {
        console.warn(`Cannot render Certificate: missing image`);
        return null;
    }
    const LinkElement = !!link ? 'a' : 'div';
    var _alt;
    return(/*#__PURE__*/ React.createElement("section", {
        className: classnames('certificates-item', {
            'certificates-item--center': !title && !text
        })
    }, /*#__PURE__*/ React.createElement(LinkElement, {
        className: "certificates-item__container",
        href: (link === null || link === void 0 ? void 0 : link.url) || undefined
    }, image && /*#__PURE__*/ React.createElement("img", {
        className: "certificates-item__image",
        src: image.url,
        alt: (_alt = image.alt) !== null && _alt !== void 0 ? _alt : ''
    }), (title || text) && /*#__PURE__*/ React.createElement("div", {
        className: "certificates-item__content"
    }, title && /*#__PURE__*/ React.createElement(Heading, {
        variant: "smallest",
        className: "certificates-item__title"
    }, title), text && /*#__PURE__*/ React.createElement("p", {
        className: "certificates-item__description"
    }, text)))));
};

const PromoteInstallmentsModalToggle = ({ onClick  })=>/*#__PURE__*/ React.createElement("button", {
        className: "tooltip__button",
        "aria-label": "tooltip",
        "data-t": "tooltip-button",
        onClick: onClick
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "questionmark"
    }))
;

const PromoteInstallmentsModal = ({ modalBodySlot  })=>{
    const { getLabel  } = useIntl();
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const onModalOpen = React.useCallback(()=>{
        setIsModalOpen(true);
    }, []);
    const onModalClose = React.useCallback(()=>setIsModalOpen(false)
    , []);
    if (!modalBodySlot) {
        return null;
    }
    const footer = /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Button, {
        caption: getLabel('installments.modal.buttonText'),
        onClick: onModalClose,
        "data-t": "footer-button",
        size: "large"
    }), /*#__PURE__*/ React.createElement(Hr, {
        className: "promote-installments-modal__horizontal-rule"
    }), /*#__PURE__*/ React.createElement("p", {
        className: "promote-installments-modal__footer-text"
    }, /*#__PURE__*/ React.createElement("small", null, getLabel('installments.modal.footerText'))));
    return(/*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(PromoteInstallmentsModalToggle, {
        onClick: onModalOpen
    }), /*#__PURE__*/ React.createElement(Modal, {
        className: "promote-installments-modal",
        isOpen: isModalOpen,
        onModalClose: onModalClose,
        showIcon: true,
        title: getLabel('installments.modal.title'),
        footer: footer
    }, modalBodySlot)));
};

const PromoteInstallmentsLabel = ({ installmentPrice , numberOfInstallments , textAlign , modalBodySlot  })=>{
    const { getFormattedLabel  } = useIntl();
    const label = React.useMemo(()=>{
        return getFormattedLabel('common.promoteInstallments.label', {
            labelData: {
                installmentPrice,
                numberOfInstallments
            }
        });
    }, [
        getFormattedLabel,
        installmentPrice,
        numberOfInstallments
    ]);
    if (!label) return null;
    return(/*#__PURE__*/ React.createElement("div", {
        className: classnames('promote-installments-label', {
            [`promote-installments-label--text-align-${textAlign}`]: textAlign
        })
    }, label, modalBodySlot && /*#__PURE__*/ React.createElement("div", {
        className: "promote-installments-label__tooltip-slot"
    }, /*#__PURE__*/ React.createElement(PromoteInstallmentsModal, {
        modalBodySlot: modalBodySlot
    }))));
};

const PromotionBanners = ({ children , fullWidth =false  })=>{
    if (!children) {
        return null;
    }
    return(/*#__PURE__*/ React.createElement("div", {
        className: classnames('promotion-banners', {
            'promotion-banners--full-width': fullWidth
        })
    }, /*#__PURE__*/ React.createElement(Container, {
        className: "promotion-banners__container"
    }, children)));
};

function _extends$2() {
    _extends$2 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$2.apply(this, arguments);
}
const PromotionBannersItem = ({ image , mobileImage , link , title , text , fullWidth =false ,  })=>{
    if (!image) {
        console.warn(`Cannot render PromotionBanner: missing image`);
        return null;
    }
    return(/*#__PURE__*/ React.createElement("a", {
        className: classnames('promotion-banners-item', {
            'promotion-banners-item--overlay': title || text,
            'promotion-banners-item--full-width': fullWidth && title && text
        }),
        href: link.url
    }, image && image.url && /*#__PURE__*/ React.createElement(LazyPicture, {
        className: "promotion-banners-item__image",
        image: image,
        mobileImage: mobileImage
    }), /*#__PURE__*/ React.createElement("div", {
        className: "promotion-banners-item__body"
    }, (title || text) && /*#__PURE__*/ React.createElement("div", {
        className: "promotion-banners-item__content"
    }, title && /*#__PURE__*/ React.createElement(Heading, {
        variant: "small",
        className: "promotion-banners-item__title"
    }, title), text && /*#__PURE__*/ React.createElement("p", {
        className: "promotion-banners-item__description"
    }, text)), link && link.label && /*#__PURE__*/ React.createElement(Button, _extends$2({
        size: "custom",
        caption: link.label,
        className: "promotion-banners-item__button"
    }, Util.createButtonStyles(link.style))))));
};

const Faq = ({ name , title , ids , children  })=>{
    const [activeId, setActiveId] = React.useState();
    const handleTitleClick = React.useCallback(()=>{
        history.replaceState(null, '', `#${name}`);
    }, [
        name
    ]);
    // Handle hash from url on mount
    React.useEffect(()=>{
        const id = window.location.hash.replace(/^#/, '');
        const hasItem = ids.find((idx)=>idx === id
        ) || name === id;
        hasItem && setActiveId(id);
    }, []);
    React.useEffect(()=>{
        if (activeId) {
            // Push onto callback queue so it runs after the DOM is updated
            setTimeout(()=>{
                const node = document.getElementById(activeId);
                if (node) {
                    const headerOffset = 200;
                    const { top  } = node.getBoundingClientRect();
                    const to = Math.round(top + window.pageYOffset) - headerOffset;
                    if (to > headerOffset) {
                        window.scrollTo(0, to);
                    }
                }
                // If the active hash is not the title, focus the related te control element
                if (activeId !== name) {
                    const ctrlNode = document.getElementById(`${activeId}-control`);
                    ctrlNode && ctrlNode.focus();
                }
            }, 250);
        }
    }, [
        activeId
    ]);
    return(/*#__PURE__*/ React.createElement(Container, null, /*#__PURE__*/ React.createElement("button", {
        className: "faq-title-wrapper",
        onClick: handleTitleClick,
        "aria-label": title
    }, /*#__PURE__*/ React.createElement(Heading, {
        id: name,
        component: "h2",
        variant: "medium"
    }, title)), /*#__PURE__*/ React.createElement(Accordion, {
        className: "faq"
    }, React.Children.map(children, (child)=>/*#__PURE__*/ React.isValidElement(child) && /*#__PURE__*/ React.cloneElement(child, {
            activeId
        })
    ))));
};

const FaqItem = ({ id , question , answer , activeId  })=>{
    const handleClick = React.useCallback((expanded)=>{
        if (expanded && id) {
            history.replaceState(null, '', `#${id}`);
        }
    }, [
        id
    ]);
    return(/*#__PURE__*/ React.createElement(AccordionItem, {
        key: id,
        id: id,
        headingLevel: "h3",
        label: question,
        expanded: id === activeId,
        onClick: handleClick
    }, /*#__PURE__*/ React.createElement(RichTextArea, {
        content: answer
    })));
};

const HighlightedContent = ({ children , className  })=>{
    if (!children) {
        return null;
    }
    return(/*#__PURE__*/ React.createElement("div", {
        className: classnames('highlighted-content', className)
    }, /*#__PURE__*/ React.createElement("div", {
        className: "highlighted-content__inner"
    }, /*#__PURE__*/ React.createElement("article", {
        className: "highlighted-content__items"
    }, children))));
};

const HighlightedContentItem = ({ image , link , title , text  })=>{
    if (!image && !text && !title) {
        console.warn(`Cannot render USP: missing content`);
        return null;
    }
    const LinkElement = !!link ? 'a' : 'div';
    return(/*#__PURE__*/ React.createElement("section", {
        className: classnames('highlighted-content-item', {
            'highlighted-content-item__with-link': !!link
        })
    }, /*#__PURE__*/ React.createElement(LinkElement, {
        className: "highlighted-content-item__link",
        href: (link === null || link === void 0 ? void 0 : link.url) || undefined
    }, image && /*#__PURE__*/ React.createElement("div", {
        className: "highlighted-content-item__image",
        style: {
            backgroundImage: `url(${image.url})`
        }
    }), /*#__PURE__*/ React.createElement("div", {
        className: "highlighted-content-item__content"
    }, title && /*#__PURE__*/ React.createElement(Heading, {
        variant: "smallest",
        component: "h3",
        className: "highlighted-content-item__title"
    }, Util.truncateString(title, 50)), text && /*#__PURE__*/ React.createElement("p", {
        className: "highlighted-content-item__description"
    }, Util.truncateString(text, 200))))));
};

const IconListItem = ({ icon , label , url  })=>{
    const WrapperElement = url ? Link : 'div';
    return(/*#__PURE__*/ React.createElement("li", {
        className: "icon-list-item"
    }, /*#__PURE__*/ React.createElement(WrapperElement, {
        className: "icon-list-item__inner",
        url: url
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: icon
    }), label && /*#__PURE__*/ React.createElement("span", {
        className: "icon-list-item__text"
    }, label))));
};

function _extends$1() {
    _extends$1 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$1.apply(this, arguments);
}
const IconList = ({ title , items =[] , theme ='bar' , className  })=>/*#__PURE__*/ React.createElement("article", {
        className: classnames('icon-list', className, {
            'icon-list--tiles': theme === 'tiles'
        })
    }, /*#__PURE__*/ React.createElement("div", {
        className: "site-container"
    }, title && /*#__PURE__*/ React.createElement(Heading, {
        component: "h3",
        className: "icon-list__title"
    }, title), /*#__PURE__*/ React.createElement("ul", {
        className: "icon-list__items"
    }, items.map((item, index)=>/*#__PURE__*/ React.createElement(IconListItem, _extends$1({}, item, {
            key: `${item.icon}-${index}`
        }))
    ))))
;
var IconList$1 = /*#__PURE__*/ React.memo(IconList);

const UspGroupItem = ({ item , className  })=>{
    const { icon , text  } = item;
    const classNames = classnames('usp-group-item', className);
    return(/*#__PURE__*/ React.createElement("div", {
        className: classNames
    }, icon && /*#__PURE__*/ React.createElement("span", {
        className: "usp-group-item__icon"
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: icon,
        width: 18,
        height: 18
    })), /*#__PURE__*/ React.createElement("span", {
        className: "usp-group-item__message"
    }, text)));
};

const UspGroup = ({ items =[] , theme , onPrevSlide , onNextSlide  })=>{
    if (!items.length) {
        return null;
    }
    return(/*#__PURE__*/ React.createElement("div", {
        className: classnames('usp-group', {
            'usp-group--solaris': theme === 'sunglasses',
            'usp-group--light': theme === 'light'
        })
    }, /*#__PURE__*/ React.createElement("div", {
        className: "usp-group__container desktop-hidden"
    }, /*#__PURE__*/ React.createElement(Carousel, {
        navigation: true,
        inverseColor: theme !== 'light',
        pagination: false,
        onPrevSlide: onPrevSlide,
        onNextSlide: onNextSlide
    }, items.map((item)=>/*#__PURE__*/ React.createElement(UspGroupItem, {
            item: item,
            key: item.text
        })
    ))), /*#__PURE__*/ React.createElement("div", {
        className: "usp-group__container mobile-hidden"
    }, items.map((item)=>/*#__PURE__*/ React.createElement(UspGroupItem, {
            item: item,
            key: item.text
        })
    ))));
};

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
const ReactPlayer = /*#__PURE__*/ React.lazy(()=>import(/* webpackChunkName: "react-player" */ 'react-player')
);
const VideoLoader = ()=>/*#__PURE__*/ React.createElement("div", {
        className: "video__loader"
    }, /*#__PURE__*/ React.createElement(SkeletonLoader, {
        className: "video__skeleton"
    }, /*#__PURE__*/ React.createElement("svg", {
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        height: "100%"
    }, /*#__PURE__*/ React.createElement("rect", {
        width: "100%",
        height: "100%",
        rx: "5",
        fill: "currentColor"
    }))))
;
const Video = /*#__PURE__*/ React.memo(({ videoUrl , coverImage  })=>{
    const { getLabel  } = useIntl();
    const playerConfig = {
        height: '100%',
        width: '100%',
        controls: true,
        playing: true
    };
    const [playing, setPlaying] = React.useState(false);
    const handleClick = React.useCallback(()=>setPlaying(true)
    , [
        setPlaying
    ]);
    if (!videoUrl || !coverImage) {
        return null;
    }
    return(/*#__PURE__*/ React.createElement("div", {
        className: "video"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "video__wrapper"
    }, playing ? /*#__PURE__*/ React.createElement("div", {
        className: "video__player"
    }, /*#__PURE__*/ React.createElement(React.Suspense, {
        fallback: /*#__PURE__*/ React.createElement(VideoLoader, null)
    }, /*#__PURE__*/ React.createElement(ReactPlayer, _extends({
        url: videoUrl
    }, playerConfig)))) : /*#__PURE__*/ React.createElement("div", {
        className: "video__image",
        style: {
            backgroundImage: `url(${coverImage.url || ''})`
        }
    }, /*#__PURE__*/ React.createElement("button", {
        "aria-label": getLabel('common.play'),
        className: "video__play-button",
        onClick: handleClick
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "play"
    }))))));
});

export { Banner, BlogCard$1 as BlogCard, BlogDetailHeader, BlogHero$1 as BlogHero, BlogIntro, BlogList, BlogMeta, BrandList, BrandListGroup, BrandListItem$1 as BrandListItem, CategoryLinks, CategoryLinksItem, Certificates, CertificatesItem, ContentBanner, ExclusiveBrands, ExclusiveBrandsItem$1 as ExclusiveBrandsItem, Faq, FaqItem, HeadTags, HighlightedContent, HighlightedContentItem, IconList$1 as IconList, IconListItem, LazyPicture, Picture, PromoteInstallmentsLabel, PromotionBanners, PromotionBannersItem, RichBanner, UspGroup, Video };
//# sourceMappingURL=index.js.map
